import { ICONS, type IconName } from './icons';

export interface MenuEntry {
  label: string;
  hint?: string;
  /**
   * Renders a check box before the label. When present the entry behaves like a switch and, with
   * `keepOpen`, the menu stays open so several can be toggled in one go.
   */
  checked?: boolean;
  /** Do not close the menu after running this entry (definitions are rebuilt from `items()`). */
  keepOpen?: boolean;
  run(): void;
}

export interface MenuButtonDef {
  icon: IconName;
  label: string;
  title: string;
  items: () => MenuEntry[];
}

let openClose: (() => void) | null = null;

/**
 * A toolbar button that drops a small menu — one control per feature area instead of a
 * row of near-identical buttons.
 */
export function createMenuButton(host: HTMLElement, def: MenuButtonDef): HTMLButtonElement {
  const wrap = document.createElement('div');
  wrap.className = 'menu-host';

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'tool file menu-trigger';
  button.title = def.title;
  button.setAttribute('aria-haspopup', 'menu');
  button.setAttribute('aria-expanded', 'false');

  const glyph = document.createElement('span');
  glyph.className = 'file-glyph';
  glyph.innerHTML = ICONS[def.icon];
  const text = document.createElement('span');
  text.textContent = def.label;
  const caret = document.createElement('span');
  caret.className = 'menu-caret';
  caret.textContent = '▾';
  button.append(glyph, text, caret);

  const panel = document.createElement('div');
  panel.className = 'menu-panel';
  panel.setAttribute('role', 'menu');
  panel.hidden = true;
  // The panel lives on the body so a toolbar that clips its overflow cannot cut it off.
  document.body.append(panel);

  /** Puts the panel under its button, nudged back inside the window when it would overflow. */
  const place = (): void => {
    const anchor = button.getBoundingClientRect();
    panel.style.left = '0px';
    panel.style.top = '0px';
    const box = panel.getBoundingClientRect();
    const left = Math.max(6, Math.min(anchor.left, window.innerWidth - box.width - 6));
    const below = anchor.bottom + 6;
    const top = below + box.height > window.innerHeight - 6 ? Math.max(6, anchor.top - box.height - 6) : below;
    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
  };

  const close = (): void => {
    if (panel.hidden) return;
    panel.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    button.classList.remove('open');
    document.removeEventListener('pointerdown', onOutside, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('resize', close);
    window.removeEventListener('scroll', close, true);
    if (openClose === close) openClose = null;
  };

  const onOutside = (event: PointerEvent): void => {
    if (!wrap.contains(event.target as Node) && !panel.contains(event.target as Node)) close();
  };

  // Document-level so Escape closes the menu even when focus is elsewhere (the canvas, a field).
  const onKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    close();
    button.focus();
    event.stopPropagation();
  };

  const build = (): void => {
    panel.replaceChildren();
    for (const entry of def.items()) {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = entry.checked === undefined ? 'menu-item' : 'menu-item is-switch';
      item.setAttribute('role', entry.checked === undefined ? 'menuitem' : 'menuitemcheckbox');
      if (entry.checked !== undefined) item.setAttribute('aria-checked', String(entry.checked));

      if (entry.checked !== undefined) {
        const tick = document.createElement('span');
        tick.className = entry.checked ? 'menu-check is-on' : 'menu-check';
        tick.textContent = entry.checked ? '✓' : '';
        item.append(tick);
        item.classList.toggle('is-on', entry.checked);
      }

      const label = document.createElement('span');
      label.className = 'menu-label';
      label.textContent = entry.label;
      item.append(label);
      if (entry.hint) {
        const hint = document.createElement('span');
        hint.className = 'menu-hint';
        hint.textContent = entry.hint;
        item.append(hint);
      }
      item.addEventListener('click', (event) => {
        event.stopPropagation();
        entry.run();
        // Switches stay open so a set of them can be flipped without re-opening the menu.
        if (entry.keepOpen) build();
        else close();
      });
      panel.append(item);
    }
  };

  button.addEventListener('click', () => {
    if (!panel.hidden) {
      close();
      return;
    }
    openClose?.();
    build();
    panel.hidden = false;
    place();
    button.setAttribute('aria-expanded', 'true');
    button.classList.add('open');
    document.addEventListener('pointerdown', onOutside, true);
    document.addEventListener('keydown', onKey, true);
    // A scrolled or resized window would leave a fixed panel behind: closing is the honest fix.
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    openClose = close;
  });

  wrap.append(button);
  host.append(wrap);
  return button;
}
